import { clsx } from 'clsx';
import { type ComponentPropsWithoutRef } from 'react';
import { BadgeV2 } from '../badge-v2/badge-v2.tsx';
import {
  CardV2,
  CardV2Body,
  CardV2Description,
  CardV2Footer,
  CardV2Header,
  CardV2Title,
} from '../card-v2/card-v2.tsx';
import { FollowersV2 } from '../followers-v2/followers-v2.tsx';
import { HandleV2, type HandleV2Props } from '../handle-v2/handle-v2.tsx';
import { ArrowRightIcon } from '../icons/react/arrow-right.tsx';
import { PlusIcon } from '../icons/react/plus.tsx';
import {
  PriceTagV2,
  type PriceTagV2Price,
} from '../price-tag-v2/price-tag-v2.tsx';
import {
  inlineCustomStreamTargets,
  inlineCustomStreamTargetsMoreLabel,
} from './inline-custom-stream-targets.ts';
import {
  streamCardV2,
  streamCardV2Body,
  streamCardV2Description,
  streamCardV2Meta,
  streamCardV2TargetChip,
  streamCardV2Targets,
  streamCardV2TargetsChips,
  streamCardV2TargetsLabel,
  streamCardV2TargetsMore,
  streamCardV2Tags,
  streamCardV2Title,
  streamCardV2View,
} from './stream-card-v2.css.ts';

export type StreamCardV2Kind = 'data' | 'custom';

const kindBadge: Record<
  StreamCardV2Kind,
  { tone: 'data' | 'custom'; label: string }
> = {
  data: { tone: 'data', label: 'Data stream' },
  custom: { tone: 'custom', label: 'Custom stream' },
};

export const StreamCardV2 = ({
  className,
  ...rest
}: ComponentPropsWithoutRef<'div'>) => (
  <CardV2
    className={clsx(streamCardV2, className)}
    {...rest}
    hoverable
    data-testid="stream-card-v2"
  />
);

export const StreamCardV2Header = ({
  kind,
  category,
  price,
  className,
}: {
  kind: StreamCardV2Kind;
  category?: string;
  price: PriceTagV2Price;
  className?: string;
}) => {
  const badge = kindBadge[kind];

  return (
    <CardV2Header className={className}>
      <div className={streamCardV2Tags}>
        <BadgeV2 tone={badge.tone}>{badge.label}</BadgeV2>
        {category ? <BadgeV2 tone="tag">{category}</BadgeV2> : null}
      </div>
      <PriceTagV2 price={price} />
    </CardV2Header>
  );
};

export const StreamCardV2Body = ({
  title,
  description,
  className,
}: {
  title: string;
  description?: string | null;
  className?: string;
}) => (
  <CardV2Body className={clsx(streamCardV2Body, className)}>
    <CardV2Title className={streamCardV2Title}>{title}</CardV2Title>
    {description ? (
      <CardV2Description className={streamCardV2Description}>
        {description}
      </CardV2Description>
    ) : null}
  </CardV2Body>
);

const TargetHintChip = ({ label }: { label?: string }) => (
  <span className={streamCardV2TargetChip}>
    <PlusIcon />
    {label ? <span>{label}</span> : null}
  </span>
);

export const StreamCardV2Targets = ({
  minTargets,
  targets,
  className,
}: {
  minTargets: number;
  targets?: readonly string[];
  className?: string;
}) => {
  const submitted = targets && targets.length > 0 ? targets : undefined;
  const inline = submitted ? inlineCustomStreamTargets(submitted) : null;
  const moreLabel = inline
    ? inlineCustomStreamTargetsMoreLabel(inline.hiddenCount)
    : null;

  return (
    <div
      className={clsx(streamCardV2Targets, className)}
      data-testid="stream-card-targets-v2"
    >
      <p className={streamCardV2TargetsLabel}>
        {submitted
          ? 'SUBMITTED TARGETS'
          : `FILL IN ${minTargets} CUSTOM TARGETS`}
      </p>
      <div className={streamCardV2TargetsChips}>
        {inline ? (
          <>
            {inline.visible.map((value, index) => (
              <span
                key={`${value}-${index}`}
                className={streamCardV2TargetChip}
              >
                {value}
              </span>
            ))}
            {moreLabel ? (
              <span className={streamCardV2TargetsMore}>{moreLabel}</span>
            ) : null}
            <TargetHintChip />
          </>
        ) : (
          <>
            <TargetHintChip label="your target" />
            <TargetHintChip label="your target" />
            <TargetHintChip />
          </>
        )}
      </div>
    </div>
  );
};

export const StreamCardV2View = ({
  children = 'View',
  className,
  ...rest
}: ComponentPropsWithoutRef<'span'>) => (
  <span className={clsx(streamCardV2View, className)} {...rest}>
    {children}
    <ArrowRightIcon />
  </span>
);

export type StreamCardV2FooterProps = {
  followers: string;
  handle: string;
  avatarSrc?: string | null;
  renderHandle?: HandleV2Props['render'];
  className?: string;
};

export const StreamCardV2Footer = ({
  followers,
  handle,
  avatarSrc,
  renderHandle,
  className,
}: StreamCardV2FooterProps) => (
  <CardV2Footer className={className}>
    <HandleV2 handle={handle} avatarSrc={avatarSrc} render={renderHandle} />
    <div className={streamCardV2Meta}>
      <FollowersV2 count={followers} />
      <StreamCardV2View />
    </div>
  </CardV2Footer>
);
